/**
 * mm-cart-drawer.js — <mm-cart-drawer>
 *
 * Open/close behaviour and trigger binding. Nothing here touches the cart;
 * mm-cart.js owns state and mm-cart-intercept.js owns adds.
 */
class MMCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
  }

  connectedCallback() {
    // The section is placed in the header group, and on transparent-header
    // pages the theme caps the z-index of every header-group child that is not
    // the header itself — which would paint the header over this overlay.
    // Reparenting to <body> takes the drawer out of that stacking context.
    // The move fires connectedCallback again, hence the guard.
    if (!this.relocated) {
      this.relocated = true;

      document.querySelectorAll('mm-cart-drawer').forEach((existing) => {
        // A theme-editor re-render injects a fresh drawer; drop the stale one.
        if (existing !== this && existing.parentElement === document.body) existing.remove();
      });

      document.body.appendChild(this);
      return;
    }

    this.init();
  }

  init() {
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    document.addEventListener('keyup', this.onDocumentKeyup);

    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());

    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onDocumentKeyup);
  }

  onDocumentKeyup = (event) => {
    if (event.code === 'Escape' && this.classList.contains('active')) this.close();
  };

  /**
   * Binds every element that should open the drawer. Idempotent — re-running it
   * after a header re-render only picks up the new nodes.
   */
  setHeaderCartIconAccessibility() {
    const triggers = document.querySelectorAll(
      '#cart-icon-bubble, a[href$="/cart"], [data-mm-cart-trigger]'
    );

    triggers.forEach((trigger) => {
      if (trigger.dataset.mmCartBound === 'true') return;
      trigger.dataset.mmCartBound = 'true';

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');

      trigger.addEventListener('click', (event) => {
        // On /cart itself the link should still navigate.
        if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;

        event.preventDefault();
        event.stopPropagation();
        this.show(trigger);
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;

        event.preventDefault();
        this.show(trigger);
      });
    });
  }

  show(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // The panel starts `visibility: hidden` at translateX(100%); it needs a
    // committed starting frame or the slide-in snaps. Reading a layout property
    // forces that flush synchronously.
    //
    // requestAnimationFrame would also do it, but its callback never runs while
    // the browser isn't painting the tab (backgrounded, occluded, automated),
    // which silently left the drawer closed on a confirmed add.
    void this.offsetWidth;

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    this.dispatchEvent(new CustomEvent('mm-cart-drawer:open', { bubbles: true }));
  }

  close() {
    this.classList.remove('active', 'is-busy');
    document.body.classList.remove('overflow-hidden');

    if (this.activeElement instanceof HTMLElement) {
      this.activeElement.focus?.();
    }

    this.dispatchEvent(new CustomEvent('mm-cart-drawer:close', { bubbles: true }));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('mm-cart-drawer')) {
  customElements.define('mm-cart-drawer', MMCartDrawer);
}
